import axios from "axios";
import { useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useAsync } from "react-use";
import useAsyncEffect from "system/useAsyncEffect";
import { Box, CircularProgress } from "@mui/material";
import Layout from "Components/Layout/Layout";
import TextBlock from "Components/Layout/TextBlock";
import VinDisplay from "Components/VinDisplay";
import VehicleNavigation from "Navigation/VehicleNavigation";
import FormWizard, { Step } from "Components/FormWizard";
import showMessage from "Dialogs/showMessage";
import useForm from "system/useForm";
import DAP01Vin from "Pages/Vehicles/Shipping/DAP/DAP01Vin";
import DAP02ConditionAndEquipment from "Pages/Vehicles/Shipping/DAP/DAP02ConditionAndEquipment";
import DAP03KeysAndAccessories from "Pages/Vehicles/Shipping/DAP/DAP03KeysAndAccessories";
import DAP04Wheels from "Pages/Vehicles/Shipping/DAP/DAP04Wheels";
import DAP05RemarksInterior from "Pages/Vehicles/Shipping/DAP/DAP05RemarksInterior";
import DAP06RemarksExterior from "Pages/Vehicles/Shipping/DAP/DAP06RemarksExterior";
import DAP07RemarksOther from "Pages/Vehicles/Shipping/DAP/DAP07RemarksOther";
import implementIData from "Pages/Vehicles/Shipping/DAP/implementIData";
import { ShippingSalePartial } from "Pages/Vehicles/Shipping/DAP/ShipmentFormPage";
import { CollectionSessionElement, ReturnForm, Sale, ShipmentForm, ShipmentFormStatus, Vehicle } from "system/Domain";
import shipmentFormPreDeliveryValid from "./shipmentFormPreDeliveryValid";

// Fills in steps 1-7 of a shipment-form for a single vehicle within a collection session.
// The last two steps (Fahrzeugabholung / Unterschriften) are intentionally excluded here - they
// are filled in once for the whole session on the "finalize" step and copied to every form.
const CollectionSessionWorkPage = () => {
	const { id, saleId } = useParams<{ id: string, saleId: string }>();
	const history = useHistory();

	const [sale, setSale] = useState<ShippingSalePartial | null>();
	const [vehicle, setVehicle] = useState<Vehicle | null>(null);

	useAsync(async () => {
		const { data } = await axios.get<ShippingSalePartial>(`/api/sales/${saleId}`);
		setSale(data);
	}, [saleId]);

	useAsync(async () => {
		if (!sale) {
			setVehicle(null);
			return;
		}

		const { data } = await axios.get<Vehicle>(`/api/vehicles/${sale.vehicle.id}`);
		setVehicle(data);
	}, [sale?.vehicle.id]);

	const shipmentFormIndex = (sale?.shipping?.shipmentForms?.length ?? 0) - 1;

	const shipmentFormData = useMemo(
		() => implementIData(sale?.shipping?.shipmentForms?.[shipmentFormIndex]),
		[sale, shipmentFormIndex]
	);

	const [comparisonData, setComparisonData] = useState<ShipmentForm>(null);

	// load last ReturnForm data - to show for comparison, same as the regular (non-session) DAP form
	useAsyncEffect(async () => {
		if (!vehicle || shipmentFormData?.isReadonly()) {
			setComparisonData(null);
			return;
		}

		const { data: returnForms } = await axios.post<ReturnForm[]>('/api/orders/transports/return-forms/search', {
			vin: vehicle.vin
		});

		// (forms search returns them in descending order - and we want to take the newest one)
		if (returnForms.length > 0) {
			setComparisonData({
				status: ShipmentFormStatus.started,
				dateUpdated: returnForms[0].dateUpdated,
				dateCreated: returnForms[0].dateCreated,
				createdBy: returnForms[0].createdBy,
				updatedBy: returnForms[0].updatedBy,
				hasReturnForm: !!returnForms[0],
				conditionAndEquipment: {
					inspectionReports: returnForms[0].readiness.inspectionReports,
					correctVin: returnForms[0].readiness.correctVin,
					mileage: returnForms[0].transfer.mileage,
					serviceBooklet: returnForms[0].documentsAndEquipment.serviceBooklet,
					serviceRecord: returnForms[0].documentsAndEquipment.serviceRecord,
					radio: returnForms[0].documentsAndEquipment.radio,
					navigationDevice: returnForms[0].documentsAndEquipment.navigationDevice,
					navigationDataMedium: returnForms[0].documentsAndEquipment.navigationDataMedium
				},
				keys: {
					vehicleKeys: returnForms[0].documentsAndEquipment.keys
				},
				accessories: {
					bootCover: returnForms[0].accessories.bootCover,
					loadSecuringNet: returnForms[0].accessories.loadSecuringNet,
					isChargingCable: returnForms[0].accessories.chargingCable
				},
				installedTiresAndRims: returnForms[0].installedTiresAndRims,
				additionalTiresAndRims: returnForms[0].additionalTiresAndRims,
				suppliedTiresAndRims: returnForms[0].suppliedTiresAndRims
			});
		} else {
			setComparisonData(null);
		}
	}, [vehicle?.vin]);

	const comparisonForm = useForm({
		values: comparisonData,
		setValues: null
	});

	const steps: Step[] = [
		{ title: "Fahrgestellnummer", ui: stepProps => <DAP01Vin {...stepProps} /> },
		{ title: "Fahrzeugzustand & Ausstattung", ui: stepProps => <DAP02ConditionAndEquipment {...stepProps} /> },
		{ title: "Schlüssel & Weitere Ausstattung", ui: stepProps => <DAP03KeysAndAccessories {...stepProps} /> },
		{ title: "Rädern und Felgen", ui: stepProps => <DAP04Wheels {...stepProps} /> },
		{ title: "Reklamation Interieur", ui: stepProps => <DAP05RemarksInterior {...stepProps} /> },
		{ title: "Reklamation Exterieur", ui: stepProps => <DAP06RemarksExterior {...stepProps} /> },
		{ title: "Reklamation Anderes", ui: stepProps => <DAP07RemarksOther {...stepProps} /> }
	];

	const formSave = useCallback(async (sf: ShipmentForm) => {
		const { data: updatedSale } = await axios.put<Sale>(`/api/sales/${saleId}/shipment-form/${shipmentFormIndex}`, sf);
		setSale(updatedSale);
	}, [saleId, shipmentFormIndex]);

	const goToNextPendingOrFinish = useCallback(async () => {
		const { data: freshElements } = await axios.get<CollectionSessionElement[]>(`/api/sales/collection-sessions/${id}/elements`);

		const next = freshElements.find(e => {
			if (e.sale.id === saleId) return false;

			const forms = e.sale.shipping?.shipmentForms;
			return !shipmentFormPreDeliveryValid(forms?.[forms.length - 1]);
		});

		if (next) {
			history.push(`/vehicles/collection-sessions/${id}/work/${next.sale.id}`);
		} else {
			await showMessage({ title: "Erledigt", message: "Alle Fahrzeuge dieser Sitzung sind für die Abholung vorbereitet." });
			history.push(`/vehicles/collection-sessions/${id}`);
		}
	}, [id, saleId, history]);

	const formComplete = useCallback(async (sf: ShipmentForm) => {
		await formSave(sf);
		await goToNextPendingOrFinish();
	}, [formSave, goToNextPendingOrFinish]);

	const checkActiveStep = useCallback(async (form, active): Promise<boolean> => {
		const isCorrectVin = !!form.getValue("conditionAndEquipment.correctVin");
		if ((active === 0) && !isCorrectVin) {
			await showMessage({ title: "Fehler", message: "Fahrgestellnummer muss KORREKT und ÜBERPRÜFT sein.", color: "error" });
			return false;
		}

		return true;
	}, []);

	return (
		<Layout
			navigation={<VehicleNavigation vehicle={vehicle} />}
			title="Ausgangsprotokoll (Sammelsitzung)"
			plateNumber={sale?.plateNumber}
		>
			<Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0, height: "100%" }}>
				{sale && (
					<Box mb={2} sx={{ flexShrink: 0 }}>
						<TextBlock
							flat
							primary={sale.vehicle.type?.description}
							secondary={<VinDisplay vin={sale.vin} />}
						/>
					</Box>
				)}
				{!shipmentFormData && (
					<CircularProgress size={48} />
				)}
				{shipmentFormData && (
					<FormWizard
						key={saleId}
						dataObject={shipmentFormData}
						steps={steps}
						onSave={formSave}
						onComplete={formComplete}
						onReturn={() => history.push(`/vehicles/collection-sessions/${id}`)}
						onNextStep={checkActiveStep}
						dirtyFormMessage="Die Änderungen wurden noch nicht gespeichert, beim Verlassen der Seite gehen die Änderungen verloren."
						stepsProps={!shipmentFormData?.isReadonly() ? comparisonForm : null}
					/>
				)}
			</Box>
		</Layout>
	);
};

export default CollectionSessionWorkPage;
